import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useChatPanel } from '../lib/chatContext'
import { IMAGE_MODELS } from '../lib/models'

// Inline SVG icon components — skill rule: no emojis as UI icons.
// Sized 16x16, stroke=currentColor so they inherit neon when active.
const Icon = {
  studio: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
    </svg>
  ),
  gallery: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  ),
  datasets: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  sparkles: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    </svg>
  ),
  chat: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  dot: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="4" />
    </svg>
  ),
}

interface Command {
  id: string
  label: string
  hint?: string
  group: string
  keywords?: string
  icon: ReactNode
  action: () => void
}

// Cmd+K / Ctrl+K command palette. Mounted globally inside Layout so the
// shortcut works on every page. Commands cover navigation, chat toggling,
// and quick model switching for the Studio prompt bar.
export default function CommandPalette() {
  const navigate = useNavigate()
  const { chatOpen, openChat, toggleChat } = useChatPanel()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setActiveIndex(0)
  }, [])

  // Global hotkey: Cmd+K / Ctrl+K toggles. Escape closes.
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey
      if (isMeta && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      } else if (e.key === 'Escape' && open) {
        close()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, close])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      // next tick — input has just mounted
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  const commands = useMemo<Command[]>(() => {
    const base: Command[] = [
      {
        id: 'nav-studio',
        label: 'Go to Studio',
        hint: 'Main canvas',
        group: 'Navigation',
        keywords: 'home create generate',
        icon: Icon.studio,
        action: () => navigate('/'),
      },
      {
        id: 'nav-gallery',
        label: 'Go to Gallery',
        hint: 'Browse past generations',
        group: 'Navigation',
        keywords: 'images history past',
        icon: Icon.gallery,
        action: () => navigate('/gallery'),
      },
      {
        id: 'nav-datasets',
        label: 'Go to Datasets',
        hint: 'Custom training data',
        group: 'Navigation',
        keywords: 'lora training photos',
        icon: Icon.datasets,
        action: () => navigate('/datasets'),
      },
      {
        id: 'nav-settings',
        label: 'Go to Settings',
        hint: 'Account, credits, role',
        group: 'Navigation',
        keywords: 'profile account credits role',
        icon: Icon.settings,
        action: () => navigate('/settings'),
      },
      {
        id: 'chat-toggle',
        label: chatOpen ? 'Close Art Director chat' : 'Open Art Director chat',
        hint: 'AI conversation mode',
        group: 'Actions',
        keywords: 'chat ai assistant claude',
        icon: Icon.sparkles,
        action: () => toggleChat(),
      },
      {
        id: 'chat-new',
        label: 'Start a new chat',
        hint: 'Fresh Art Director conversation',
        group: 'Actions',
        keywords: 'new chat conversation reset',
        icon: Icon.chat,
        action: () => {
          if (!chatOpen) openChat()
          // Broadcast a reset request; ChatPanel listens and calls
          // useConversation's resetConversation() (see task #4).
          window.dispatchEvent(new CustomEvent('fv:chat-reset'))
        },
      },
      {
        // The usual — an easter egg. Typing "the usual" or "drink" or
        // "gin" in the palette reveals the art director's signature
        // cocktail page. Not advertised anywhere else; people find it.
        id: 'the-usual',
        label: 'The usual, darling',
        hint: 'Arty\'s signature drink',
        group: 'Actions',
        keywords: 'drink gin tonic rooibos naartjie cardamom cocktail the usual recipe',
        icon: Icon.sparkles,
        action: () => navigate('/drink'),
      },
    ]

    // Quick model-switch commands (jump to Studio with that model preselected)
    for (const m of IMAGE_MODELS) {
      base.push({
        id: `model-${m.id}`,
        label: `Use model: ${m.name}`,
        hint: m.description ?? `${m.default_steps} steps`,
        group: 'Models',
        keywords: `model ${m.id} ${m.name}`,
        icon: Icon.dot,
        action: () => {
          navigate('/', { state: { model: m.id } })
        },
      })
    }

    return base
  }, [navigate, chatOpen, openChat, toggleChat])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    return commands.filter((c) => {
      const hay = `${c.label} ${c.hint ?? ''} ${c.keywords ?? ''} ${c.group}`.toLowerCase()
      return hay.includes(q)
    })
  }, [commands, query])

  // Reset active index when filter changes
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  const runCommand = useCallback(
    (cmd: Command) => {
      cmd.action()
      close()
    },
    [close],
  )

  const handleInputKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const cmd = filtered[activeIndex]
        if (cmd) runCommand(cmd)
      }
    },
    [filtered, activeIndex, runCommand],
  )

  if (!open) return null

  // Group commands for rendering
  const groups: Record<string, Command[]> = {}
  filtered.forEach((c) => {
    if (!groups[c.group]) groups[c.group] = []
    groups[c.group].push(c)
  })

  // Build a flat array of indices so the active highlight matches arrow nav
  let flatIndex = -1

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4 bg-black/70 backdrop-blur-md animate-in fade-in"
      onClick={close}
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-bg-card border border-border shadow-2xl shadow-black/60 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#39FF14"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKey}
            placeholder="Search commands, models, pages…"
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
          />
          <kbd className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-border text-text-muted">
            ESC
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <p className="text-center text-xs text-text-muted py-6">
              No matches
            </p>
          ) : (
            Object.entries(groups).map(([group, cmds]) => (
              <div key={group} className="mb-1">
                <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                  {group}
                </p>
                {cmds.map((cmd) => {
                  flatIndex++
                  const isActive = flatIndex === activeIndex
                  const myIndex = flatIndex
                  return (
                    <button
                      key={cmd.id}
                      onMouseEnter={() => setActiveIndex(myIndex)}
                      onClick={() => runCommand(cmd)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                        isActive
                          ? 'bg-neon/10 border-l-2 border-neon'
                          : 'border-l-2 border-transparent'
                      }`}
                    >
                      <span
                        className={`shrink-0 w-5 flex items-center justify-center ${
                          isActive ? 'text-neon' : 'text-text-muted'
                        }`}
                      >
                        {cmd.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm truncate ${
                            isActive ? 'text-text-primary' : 'text-text-secondary'
                          }`}
                        >
                          {cmd.label}
                        </p>
                        {cmd.hint && (
                          <p className="text-[11px] text-text-muted truncate">
                            {cmd.hint}
                          </p>
                        )}
                      </div>
                      {isActive && (
                        <kbd className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-neon/40 text-neon shrink-0">
                          ↵
                        </kbd>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-bg-secondary">
          <div className="flex items-center gap-3 text-[10px] text-text-muted">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded border border-border">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded border border-border">↵</kbd>
              select
            </span>
          </div>
          <span className="text-[10px] text-text-muted font-mono">FRANKENVIBES</span>
        </div>
      </div>
    </div>
  )
}
